import { Link as RouterLink, generatePath } from 'react-router';

import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_CREDIT_CARD_LOAN } from './RRS.ts';

const columns = ['Renter / Ubetalt gjeld', '100.000 kr', '250.000 kr', '400.000 kr'];

const rows = [
  {
    title: 'Renteutgifter per år – kredittkortlån 25%',
    values: ['25.000 kr', '62.500 kr', '100.000 kr'],
  },
  {
    title: 'Renteutgifter per år – refinansiering 10%',
    values: ['10.000 kr', '25.000 kr', '40.000 kr'],
  },
];

const firstColCell =
  'typography-v1-legacy-table-cell sticky left-0 z-10 w-[130px] min-w-[130px] border border-[#CEE2FF] bg-white px-4! py-2! text-left font-normal min-[500px]:w-auto min-[500px]:min-w-0';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const CreditCardLoanTable = () => (
  <div className="relative mx-auto mb-12 w-full max-w-[900px] overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_4px_8px_rgba(24,160,251,0.12)]">
    <table className="w-full border-collapse border-hidden">
      <thead>
        <tr className="align-top">
          {columns.map((column, index) => {
            const isFirstColumn = index === 0;
            return (
              <th
                key={column}
                scope="col"
                className={
                  isFirstColumn
                    ? `${firstColCell} relative text-v1-background-200`
                    : 'min-w-[115px] border border-[#CEE2FF] px-4! py-2! text-right typography-v1-legacy-table-cell font-normal text-v1-background-200'
                }
              >
                {isFirstColumn && (
                  <span
                    className="absolute top-[-8px] left-[130px] h-[298px] w-px bg-[#CEE2FF] min-[500px]:hidden"
                    aria-hidden
                  />
                )}
                {column}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ title, values }) => (
          <tr key={title} className="align-top">
            <th scope="row" className={`${firstColCell} text-v1-background-200`}>
              {title}
            </th>
            {values.map((value) => (
              <td
                key={value}
                className="min-w-[115px] border border-[#CEE2FF] px-4! py-2! text-right typography-v1-legacy-table-cell text-[#18A0FB]"
              >
                {value}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CreditCardLoan = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.CREDIT_CARD_LOAN}>
      <RrsHandler {...RRS_CREDIT_CARD_LOAN} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.CREDIT_CARD_LOAN },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Kredittkortlån? Da kan det lønne seg å refinansiere
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Har du flere kredittkort med mye gjeld? Du kan spare titusener hvert år på å
            refinansiere til ett lån – det tar deg kun to minutter å søke.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Kredittkort kan være smart dersom du ikke bruker mer penger enn du har råd til, og
            betaler regningene i tide. Betaler du når du skal er kredittkort i prinsippet gratis lån
            – ulempen er at noen anser kredittkort som gratis penger, og det er da de økonomiske
            problemene oppstår.
          </p>
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Reduser dine lånekostnader nå!
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className="flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row">
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Lån for å betale kredittkort?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Å opparbeide gjeld på kredittkort skjer gjerne uten at du legger merke til det, og
              plutselig har den vokst seg så stor at du ikke klarer å betale regningene ved forfall.
              Dette skyldes ofte det som kalles rentebærende saldo, som er det utestående beløpet du
              må betale renter på – altså de pengene du ikke har betalt innen forfall.
              <br />
              <br />
              Et eksempel på rentebærende saldo er dersom du kjøper en sofa til 25.000 kroner med
              kredittkort. Når regningen kommer, betaler du kun 5.000 kroner tilbake. Da må du
              betale renter på de resterende 20.000 kronene, og dette blir dermed den rentebærende
              saldoen på neste regning. Hvis du fortsetter å utsette betalingen og renten på
              kredittkortet er 25 prosent, koster sofaen deg 5.000 kroner ekstra i året.
              <br />
              <br />
              Har du flere kredittkortlån, er det fort gjort at mange slike små beløp kan vokse til
              én stor og uoverkommelig sum. Og det er her det er vanlig å gå i den velkjente
              luksusfellen – man tar opp nye lån eller kredittkort for å betale gammel moro. Ulempen
              er at når du gjør dette utsetter du bare problemet, i tillegg til at det vokser seg
              stadig større.
              <br />
              <br />
              Det du isteden bør gjøre er å samle alle kredittkortene gjennom å refinansiere.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Refinansiere kredittkortlån – så mye kan du spare
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Med refinansiering får du som regel mye bedre betingelser enn du gjør med kredittkort.
              Blant annet får du lavere rente, færre gebyrer, én bank å forholde deg til og én
              månedlig regning. Det gjør at du slipper unødvendige og fordyrende kostnader, samtidig
              som du får bedre oversikt over økonomien din.
              <br />
              <br />
              La oss si at du har flere kredittkortlån med en gjennomsnittlig rente på nevnte 25
              prosent. I tabellen ser du hvor mye du sparer i renteutgifter hvert år når du
              refinansierer kredittkortene dine.
            </p>
          </div>
          <div className="mx-auto block w-full max-w-[900px] sm:hidden">
            <CreditCardLoanTable />
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Som du ser – har du mange kredittkort med rentebærende saldo er det stor sannsynlighet
              for at det er en god idé å refinansiere, da dette vil redusere rentekostnadene dine
              med tusenvis av kroner hver måned. Er du flink og bruker pengene du sparer til å
              betale ned lånet raskere, blir du samtidig fortere gjeldfri. Det må jo kunne kalles en
              vinn-vinn-situasjon.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Refinansiere kredittkortlån – så enkelt gjør du det med Scoopr
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Nær sagt uansett hvor stor kredittkortgjeld du har, kan vi i Scoopr hjelpe deg – hos
              oss kan du søke om{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                refinansiering fra 20.000 til 500.000 kroner
              </RouterLink>
              . Det er helt gratis og uforpliktende, og koster deg ikke annet enn to minutter av
              tiden din. Å søke om refinansiering hos Scoopr kan med andre ord gi deg en veldig god
              timelønn.
              <br />
              <br />
              Det eneste du må gjøre er å ha BankID klar, og så fylle ut{' '}
              <RouterLink
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
                className={linkClassName}
              >
                dette skjemaet
              </RouterLink>
              . Når du har gjort det, tar Scoopr over. Da blir søknaden din automatisk sendt ut til
              13 banker, og siden de konkurrerer om å få deg som kunde gjør de sitt ytterste for å
              gi deg så lav rente som mulig.
              <br />
              <br />
              Du får svar på søknaden din i løpet av kort tid, og så kan du i ro og mak vurdere
              hvilket av tilbudene som passer deg best. Når du har valgt banken din betaler de
              vanligvis kredittkortgjelden din, slik at du ikke behøver å tenke mer på denne. Det
              betyr at du om kun noen få dager kan få en fremtid med mer kontroll og bedre oversikt
              over økonomien din.
              <br />
              <br />
              <strong>
                Lurer du på hvor mye du kan spare på å refinansiere kredittkortene dine?{' '}
                <RouterLink to={routes.HOME} className={linkClassName}>
                  Da kan du sjekke det her.
                </RouterLink>
              </strong>
            </p>
          </div>
        </div>
        {/* Keeps vertical rhythm vs legacy layout between two-column block and desktop table (sm–lg). */}
        <div className="mx-auto hidden w-full max-w-[900px] sm:mt-6 sm:block lg:mt-0">
          <CreditCardLoanTable />
        </div>

        <div className="flex flex-col items-center [&>button]:self-center">
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Reduser dine lånekostnader nå!
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default CreditCardLoan;
